import { Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ListaAcaoEngrenagem } from '../components/models/lista-acao-engrenagem.model';
import { AutorizacaoManualModel, toAutorizacaoManualModel } from '../model/autorizacao-manual.model';
import {
  extrairMensagemSwapAutorizacaoManual,
  isSwapAutorizacaoManualOk,
  SwapGravarSessaoRequest,
  SwapGravarSessaoResponse,
  SwapSessao,
} from '../model/swap';
import { SwapService } from '../services/swap.service';
import { FlashMessageService } from '../services/flash-message.service';

const MAX_TENTATIVAS_SESSAO_DUPLICADA = 3;

/**
 * Monta as ações do menu de engrenagem da lista de autorização manual:
 *  - **Repetir solicitação**: somente quando autorizado = Nao (`repetirDisponivel`:
 *    `autorizado === false`). NULL ou Sim ocultam a acao. Navega para `repetir/:dscSessionExterna`.
 *  - **Bypass**: somente quando o DTO indica bypass permitido (`habilitaByPass`;
 *    `toAutorizacaoManualModel` copia para `flgPermiteBypass` / `bypassDisponivel`).
 *    Chaves antigas (`FLG_PERMITE_BYPASS`, …) ainda são aceitas ao parsear o DTO.
 *    Orquestra `obterSessao` → `gravarSessao` (uuid novo, `bypass=true`) → `incluirAutorizacaoManual`.
 *
 * A coluna "Ação" da grade usa `bypassDisponivel` (mesma regra do bypass
 * na engrenagem).
 *
 * `recarregarLista` é chamado após sucesso do bypass.
 * `loadingInicio`/`loadingFim` integram o spinner global na operação de bypass.
 */
export function criarAcoesAutorizacaoManualListaRotas(
  router: Router,
  swapService: SwapService,
  flash: FlashMessageService,
  obterGuidUsuario: () => string,
  recarregarLista: () => void,
  loadingInicio: () => void,
  loadingFim: () => void,
): ListaAcaoEngrenagem<AutorizacaoManualModel>[] {
  return [
    {
      id: 'repetir',
      rotulo: 'Repetir solicitação',
      exibicao: (item) => (item.repetirDisponivel ? 'habilitado' : 'oculto'),
      aoExecutar: (item) => {
        void router.navigate([
          '/',
          'autorizacao-manual',
          'repetir',
          item.dscSessionExterna,
        ]);
      },
    },
    {
      id: 'bypass',
      rotulo: 'Bypass',
      exibicao: (item) => (item.bypassDisponivel ? 'habilitado' : 'oculto'),
      aoExecutar: (item) => {
        executarBypassDeListagem(
          item,
          swapService,
          flash,
          obterGuidUsuario(),
          recarregarLista,
          loadingInicio,
          loadingFim,
        );
      },
    },
  ];
}

function executarBypassDeListagem(
  item: AutorizacaoManualModel,
  swapService: SwapService,
  flash: FlashMessageService,
  guidUsuario: string,
  recarregarLista: () => void,
  loadingInicio: () => void,
  loadingFim: () => void,
): void {
  if (!item.dscSessionExterna) {
    flash.erro('Sessão sem identificador externo. Não foi possível aplicar o bypass.');
    return;
  }

  loadingInicio();
  swapService
    .obterSessao(guidUsuario, item.dscSessionExterna)
    .pipe(
      switchMap((res) => {
        if (!res?.sucesso || !res.retorno) {
          return throwError(() => new Error('Registro original não encontrado.'));
        }
        const requestBase = montarRequestBaseDeSwap(res.retorno);
        if (!requestBase.codMotorista || !requestBase.pawPlaca) {
          return throwError(
            () =>
              new Error(
                'Registro original incompleto (motorista/placa). Não é possível aplicar bypass.',
              ),
          );
        }
        return gravarComRetry(
          swapService,
          guidUsuario,
          requestBase,
          MAX_TENTATIVAS_SESSAO_DUPLICADA,
        );
      }),
      switchMap((respostaGravacao) => {
        publicarFlashesPosGravacao(flash, respostaGravacao);
        const sessionExterna = respostaGravacao.sessionExterna ?? '';
        return swapService.incluirAutorizacaoManual(guidUsuario, { sessionExterna });
      }),
      catchError((err) => {
        loadingFim();
        flash.erro(formatarErro(err, 'Falha ao aplicar bypass.'));
        return EMPTY;
      }),
    )
    .subscribe((resp) => {
      loadingFim();
      if (isSwapAutorizacaoManualOk(resp)) {
        flash.sucesso('Autorização manual enviada.');
        recarregarLista();
        return;
      }
      // HTTP 200 + statusRetorno NEGADO/ERRO: erro de negócio da BIS-SWAP.
      // Não recarrega a lista; o item segue como estava e mostra a mensagem.
      flash.erro(
        extrairMensagemSwapAutorizacaoManual(resp) ??
          'Autorização manual não foi efetivada.',
      );
    });
}

/**
 * Publica os flashes apropriados após a gravação no fluxo de bypass
 * pela listagem (foco é o bypass, não a gravação):
 *  - Bypass aplicado     → 1 flash verde "Bypass aplicado com sucesso."
 *  - Bypass falhou        → 1 flash verde da gravação + 1 flash amarelo
 *                            com `bypass.dscRetorno` (sessão ficou na
 *                            lista, mas o bypass não pegou).
 *  - bypass null          → não deveria ocorrer aqui (sempre enviamos
 *                            `bypass=true`); fallback mostra apenas o
 *                            sucesso da gravação.
 */
function publicarFlashesPosGravacao(
  flash: FlashMessageService,
  resp: SwapGravarSessaoResponse,
): void {
  if (resp.bypass) {
    if (resp.bypass.flgSucesso) {
      flash.sucesso(resp.bypass.dscRetorno?.trim() || 'Bypass aplicado com sucesso.');
    } else {
      flash.sucesso('Sessão gravada.');
      flash.aviso(resp.bypass.dscRetorno?.trim() || 'Bypass não aplicado.');
    }
    return;
  }
  flash.sucesso('Sessão gravada.');
}

function gravarComRetry(
  swapService: SwapService,
  guidUsuario: string,
  requestBase: Omit<SwapGravarSessaoRequest, 'dscSessionExterna'>,
  tentativas: number,
): Observable<SwapGravarSessaoResponse> {
  const request: SwapGravarSessaoRequest = {
    ...requestBase,
    dscSessionExterna: gerarUuid(),
  };
  return swapService.gravarSessao(guidUsuario, request).pipe(
    switchMap((res) => {
      if (res?.proximaEtapa === 'SESSAO_EXISTENTE') {
        if (tentativas > 1) {
          return gravarComRetry(swapService, guidUsuario, requestBase, tentativas - 1);
        }
        return throwError(
          () => new Error('Não foi possível gerar uma sessão única. Tente novamente.'),
        );
      }
      return [res];
    }),
  );
}

function montarRequestBaseDeSwap(
  swap: SwapSessao,
): Omit<SwapGravarSessaoRequest, 'dscSessionExterna'> {
  const ui = toAutorizacaoManualModel(swap);
  return {
    codMotorista: Number(ui.codMotorista ?? 0),
    pawPlaca: (ui.placa ?? '').toUpperCase().replace(/[^A-Z0-9]/g, ''),
    vlrPreco: Number(ui.precoUnitario ?? 0),
    qtdVolume: Number(ui.litros ?? 0),
    vlrTotal: Number(ui.valorTotal ?? 0),
    numKm: Math.trunc(Number(ui.hodometro ?? 0)),
    cdProduto: ui.cdProduto || undefined,
    /*
     * Bypass pela listagem é SEMPRE solicitado: o objetivo desta
     * ação é justamente autorizar uma solicitação que deu erro.
     */
    bypass: true,
  };
}

function gerarUuid(): string {
  const c =
    typeof crypto !== 'undefined'
      ? (crypto as Crypto & { randomUUID?: () => string })
      : undefined;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function formatarErro(err: unknown, fallback: string): string {
  const e = err as { error?: { dscRetorno?: string; message?: string }; message?: string };
  return e?.error?.dscRetorno || e?.error?.message || e?.message || fallback;
}
